import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import {
  CreditCard,
  Calendar,
  DollarSign,
  Clock,
  AlertCircle,
  CheckCircle,
  Pause,
  Play,
  XCircle,
  ArrowLeft,
  RefreshCw
} from 'lucide-react';
import { api } from '../services/api';
import { useToast } from '../contexts/ToastContext';

interface Subscription {
  id: number;
  user_id: number;
  plan_id: number;
  mercadopago_preapproval_id: string;
  status: string;
  amount?: number;
  frequency?: number;
  frequency_type?: string;
  start_date: string;
  end_date: string | null;
  next_payment_date?: string;
  next_billing_date?: string;
  expires_at?: string;
  created_at: string;
  plan?: {
    name: string;
    description: string;
    price?: number;
  };
}

interface SubscriptionDetails {
  id: string;
  status: string;
  reason: string;
  external_reference: string;
  payer_id: number;
  auto_recurring: {
    frequency: number;
    frequency_type: string;
    transaction_amount: number;
    currency_id: string;
    start_date: string;
    end_date: string | null;
  };
  date_created: string;
  last_modified: string;
}

export const SubscriptionManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [details, setDetails] = useState<SubscriptionDetails | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);

  const loadSubscriptionData = useCallback(async () => {
    try {
      setLoading(true);

      // Buscar assinatura ativa
      const subResponse = await api.get('/billing/subscription');

      if (subResponse.data.success && subResponse.data.data?.subscription) {
        const sub = subResponse.data.data.subscription;
        setSubscription(sub);

        // Buscar detalhes completos do MercadoPago
        if (sub.id) {
          const detailsResponse = await api.get(`/payments/subscription/${sub.id}/details`);
          if (detailsResponse.data.success) {
            setDetails(detailsResponse.data.data);
          }
        }
      }
    } catch (error: any) {
      console.error('Erro ao carregar assinatura:', error);
      if (error.response?.status !== 404) {
        showError('Erro ao carregar dados da assinatura');
      }
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    loadSubscriptionData();
  }, [loadSubscriptionData]);

  const handlePauseSubscription = async () => {
    if (!subscription) return;

    try {
      setActionLoading(true);
      const response = await api.post('/payments/subscription/pause', {
        subscription_id: subscription.id
      });

      if (response.data.success) {
        showSuccess('Assinatura pausada com sucesso!');
        loadSubscriptionData();
      }
    } catch (error: any) {
      showError(error.response?.data?.message || 'Erro ao pausar assinatura');
    } finally {
      setActionLoading(false);
    }
  };

  const handleResumeSubscription = async () => {
    if (!subscription) return;

    try {
      setActionLoading(true);
      const response = await api.post('/payments/subscription/resume', {
        subscription_id: subscription.id
      });

      if (response.data.success) {
        showSuccess('Assinatura reativada com sucesso!');
        loadSubscriptionData();
      }
    } catch (error: any) {
      showError(error.response?.data?.message || 'Erro ao reativar assinatura');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!subscription) return;

    try {
      setActionLoading(true);
      const response = await api.post('/payments/subscription/cancel', {
        subscription_id: subscription.id
      });

      if (response.data.success) {
        showSuccess('Assinatura cancelada com sucesso');
        setShowCancelModal(false);
        navigate('/billing');
      }
    } catch (error: any) {
      showError(error.response?.data?.message || 'Erro ao cancelar assinatura');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusInfo = (status: string) => {
    const statusMap: Record<string, { label: string; color: string; icon: any }> = {
      authorized: { label: 'Ativa', color: '#00d4aa', icon: CheckCircle },
      paused: { label: 'Pausada', color: '#ffa500', icon: Pause },
      cancelled: { label: 'Cancelada', color: '#ff4757', icon: XCircle },
      pending: { label: 'Pendente', color: '#ffa500', icon: Clock }
    };

    return statusMap[status] || { label: status, color: '#666', icon: AlertCircle };
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) {
      return '-';
    }
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatCurrency = (value?: number | string) => {
    if (value === undefined || value === null || value === '') {
      return '-';
    }

    const numeric = typeof value === 'string'
      ? Number(
          value.includes(',')
            ? value.replace(/\./g, '').replace(',', '.')
            : value
        )
      : value;

    if (Number.isNaN(numeric)) {
      return '-';
    }

    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(numeric);
  };

  const getFrequencyText = (frequency?: number, type?: string) => {
    if (!frequency || !type) {
      return '1 mês';
    }
    const typeMap: Record<string, string> = {
      months: 'mês',
      days: 'dia',
      years: 'ano'
    };

    return `${frequency} ${typeMap[type] || type}`;
  };

  if (loading) {
    return (
      <Container>
        <LoadingSpinner>
          <RefreshCw size={40} />
          <p>Carregando dados da assinatura...</p>
        </LoadingSpinner>
      </Container>
    );
  }

  if (!subscription) {
    return (
      <Container>
        <EmptyState>
          <AlertCircle size={60} />
          <h2>Nenhuma assinatura ativa</h2>
          <p>Você não possui uma assinatura ativa no momento.</p>
          <Button onClick={() => navigate('/plans')}>
            Ver Planos Disponíveis
          </Button>
        </EmptyState>
      </Container>
    );
  }

  const statusInfo = getStatusInfo(subscription.status);
  const StatusIcon = statusInfo.icon;
  const displayAmount = subscription.amount ?? subscription.plan?.price ?? details?.auto_recurring?.transaction_amount;
  const displayNextPayment = subscription.next_payment_date
    || subscription.next_billing_date
    || subscription.expires_at
    || details?.auto_recurring?.start_date;
  const displayFrequency = subscription.frequency ?? details?.auto_recurring?.frequency ?? 1;
  const displayFrequencyType = subscription.frequency_type ?? details?.auto_recurring?.frequency_type ?? 'months';

  return (
    <Container>
      <Header>
        <BackButton onClick={() => navigate('/billing')}>
          <ArrowLeft size={20} />
          Voltar para Cobrança
        </BackButton>
        <Title>Gerenciamento de Assinatura</Title>
      </Header>

      <Content>
        <Section>
          <SectionHeader>
            <h2>Status da Assinatura</h2>
            <StatusBadge color={statusInfo.color}>
              <StatusIcon size={16} />
              {statusInfo.label}
            </StatusBadge>
          </SectionHeader>

          <InfoGrid>
            <InfoCard>
              <InfoIcon>
                <CreditCard size={24} />
              </InfoIcon>
              <InfoContent>
                <InfoLabel>Plano Atual</InfoLabel>
                <InfoValue>{subscription.plan?.name || 'Plano Premium'}</InfoValue>
              </InfoContent>
            </InfoCard>

            <InfoCard>
              <InfoIcon>
                <DollarSign size={24} />
              </InfoIcon>
              <InfoContent>
                <InfoLabel>Valor Mensal</InfoLabel>
                <InfoValue>{formatCurrency(displayAmount)}</InfoValue>
              </InfoContent>
            </InfoCard>

            <InfoCard>
              <InfoIcon>
                <Calendar size={24} />
              </InfoIcon>
              <InfoContent>
                <InfoLabel>Próximo Pagamento</InfoLabel>
                <InfoValue>{formatDate(displayNextPayment)}</InfoValue>
              </InfoContent>
            </InfoCard>

            <InfoCard>
              <InfoIcon>
                <Clock size={24} />
              </InfoIcon>
              <InfoContent>
                <InfoLabel>Frequência</InfoLabel>
                <InfoValue>A cada {getFrequencyText(displayFrequency, displayFrequencyType)}</InfoValue>
              </InfoContent>
            </InfoCard>
          </InfoGrid>
        </Section>

        {details && (
          <Section>
            <SectionHeader>
              <h2>Detalhes Adicionais</h2>
            </SectionHeader>

            <DetailsList>
              <DetailItem>
                <DetailLabel>Data de Início:</DetailLabel>
                <DetailValue>{formatDate(details.auto_recurring.start_date)}</DetailValue>
              </DetailItem>
              {details.auto_recurring.end_date && (
                <DetailItem>
                  <DetailLabel>Data de Término:</DetailLabel>
                  <DetailValue>{formatDate(details.auto_recurring.end_date)}</DetailValue>
                </DetailItem>
              )}
              <DetailItem>
                <DetailLabel>ID MercadoPago:</DetailLabel>
                <DetailValue>{subscription.mercadopago_preapproval_id}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Última Atualização:</DetailLabel>
                <DetailValue>{formatDate(details.last_modified)}</DetailValue>
              </DetailItem>
            </DetailsList>
          </Section>
        )}

        <Section>
          <SectionHeader>
            <h2>Ações</h2>
          </SectionHeader>

          <ActionsGrid>
            {subscription.status === 'authorized' && (
              <ActionButton
                color="#ffa500"
                onClick={handlePauseSubscription}
                disabled={actionLoading}
              >
                <Pause size={20} />
                Pausar Assinatura
              </ActionButton>
            )}

            {subscription.status === 'paused' && (
              <ActionButton
                color="#00d4aa"
                onClick={handleResumeSubscription}
                disabled={actionLoading}
              >
                <Play size={20} />
                Retomar Assinatura
              </ActionButton>
            )}

            {subscription.id && (
              <ActionButton
                color="#666"
                onClick={() => navigate(`/billing/invoices/${subscription.id}`)}
              >
                <Calendar size={20} />
                Ver Histórico de Faturas
              </ActionButton>
            )}

            <ActionButton
              color="#666"
              onClick={() => navigate('/billing/cards')}
            >
              <CreditCard size={20} />
              Gerenciar Cartões
            </ActionButton>

            {subscription.status !== 'cancelled' && (
              <ActionButton
                color="#ff4757"
                onClick={() => setShowCancelModal(true)}
                disabled={actionLoading}
              >
                <XCircle size={20} />
                Cancelar Assinatura
              </ActionButton>
            )}
          </ActionsGrid>
        </Section>
      </Content>

      {showCancelModal && (
        <Modal>
          <ModalOverlay onClick={() => setShowCancelModal(false)} />
          <ModalContent>
            <ModalHeader>
              <AlertCircle size={24} color="#ff4757" />
              <h3>Cancelar Assinatura</h3>
            </ModalHeader>
            <ModalBody>
              <p>Tem certeza que deseja cancelar sua assinatura?</p>
              <p>Esta ação não pode ser desfeita. Você perderá acesso aos recursos do plano ao final do período pago.</p>
            </ModalBody>
            <ModalFooter>
              <CancelButton onClick={() => setShowCancelModal(false)} disabled={actionLoading}>
                Voltar
              </CancelButton>
              <ConfirmButton onClick={handleCancelSubscription} disabled={actionLoading}>
                {actionLoading ? 'Cancelando...' : 'Sim, Cancelar'}
              </ConfirmButton>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
    </Container>
  );
};

const Container = styled.div`
  padding: 2rem;
  width: 100%;
  max-width: none;
  margin: 0;
  min-height: 100vh;
  background: #f8f9fa;
`;

const Header = styled.div`
  margin-bottom: 2rem;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: #00d4aa;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  cursor: pointer;
  margin-bottom: 1rem;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.8;
  }
`;

const Title = styled.h1`
  font-size: 2rem;
  color: #1f2937;
  margin: 0;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const Section = styled.div`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 2rem;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;

  h2 {
    font-size: 1.3rem;
    color: #1f2937;
    margin: 0;
  }
`;

const StatusBadge = styled.div<{ color: string }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: ${props => props.color}20;
  color: ${props => props.color};
  border: 1px solid ${props => props.color};
  border-radius: 20px;
  font-weight: 600;
  font-size: 0.9rem;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
`;

const InfoCard = styled.div`
  display: flex;
  gap: 1rem;
  padding: 1.5rem;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
`;

const InfoIcon = styled.div`
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 212, 170, 0.1);
  border-radius: 8px;
  color: #00d4aa;
  flex-shrink: 0;
`;

const InfoContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
`;

const InfoLabel = styled.span`
  font-size: 0.85rem;
  color: #64748b;
`;

const InfoValue = styled.span`
  font-size: 1.1rem;
  color: #0f172a;
  font-weight: 600;
`;

const DetailsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const DetailItem = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 1rem;
  background: #f8fafc;
  border-radius: 6px;
`;

const DetailLabel = styled.span`
  color: #64748b;
  font-size: 0.95rem;
`;

const DetailValue = styled.span`
  color: #0f172a;
  font-weight: 500;
`;

const ActionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
`;

const ActionButton = styled.button<{ color: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 1rem 1.5rem;
  background: ${props => props.color}20;
  border: 1px solid ${props => props.color};
  color: ${props => props.color};
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: ${props => props.color}30;
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  min-height: 400px;
  color: #00d4aa;

  svg {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  p {
    color: #64748b;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  min-height: 400px;
  text-align: center;
  color: #999;

  svg {
    color: #666;
  }

  h2 {
    color: #0f172a;
    margin: 0;
  }

  p {
    margin: 0;
  }
`;

const Button = styled.button`
  margin-top: 1rem;
  padding: 0.75rem 1.5rem;
  background: #00d4aa;
  color: #0a0e17;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 212, 170, 0.3);
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
`;

const ModalContent = styled.div`
  position: relative;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 2rem;
  max-width: 500px;
  width: 90%;
  z-index: 1001;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;

  h3 {
    color: #1f2937;
    margin: 0;
  }
`;

const ModalBody = styled.div`
  margin-bottom: 2rem;

  p {
    color: #475569;
    margin-bottom: 1rem;
    line-height: 1.6;

    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const ModalFooter = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
`;

const CancelButton = styled.button`
  padding: 0.75rem 1.5rem;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  color: #1f2937;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: #e2e8f0;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ConfirmButton = styled.button`
  padding: 0.75rem 1.5rem;
  background: #ff4757;
  border: none;
  color: #fff;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: #ff3444;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
